import { KURUMSAL_KIMLIK } from '@/shared/constants/kurumsalKimlik';

/**
 * Schema.org Organization JSON-LD builder.
 *
 * 11+ public sayfa (index, ozellikler, isg-yazilimi, araclar, blog, ornekler,
 * risk-metodolojileri, saha-gozlem/ornekler ...) inline Organization JSON-LD
 * tanımlıyordu. Tek SSoT helper — drift bitti.
 *
 * legalName SCHEMA'da bilinçli olarak marka adıyla aynı. Kişi kimliği (Ali
 * Zorlu) Schema.org'da public görünür (Google Knowledge Graph'a düşer); bu
 * yüzden Katman B kuralı: marka önde, kişi geri planda. Yasal mükellef
 * kimliği KVKK + Kullanım Koşulları sayfa İÇERİĞİNDE mevzuat zorunluluğu
 * gereği görünür (Katman A).
 */

type OrgOverrides = Record<string, unknown>;

interface OrganizationIdentifier {
  '@type': 'PropertyValue';
  propertyID: string;
  name?: string;
  value: string;
  url?: string;
  description?: string;
}

interface OrganizationSchema {
  '@context': string;
  '@type': 'Organization';
  name: string;
  /**
   * Disambiguation — 2008 yılında Türk Kardiyoloji Derneği tarafından kalp riski
   * SMS programı için "Riskmatik" ismi kullanılmıştı. AI'lara şu anki ürünü
   * net işaret eden alternate name.
   */
  alternateName?: string;
  legalName: string;
  url: string;
  logo: string;
  foundingDate: string;
  description: string;
  /**
   * Slogan — brand identity sinyali. AI Knowledge Panel + arama sonuç
   * snippet'larında kısa açıklama olarak görünebilir.
   */
  slogan?: string;
  /**
   * Schema.org Organization.sameAs — Aynı entity'nin başka kaynaklardaki
   * URL'leri. Google Knowledge Graph + AI search (ChatGPT/Perplexity/Gemini)
   * entity disambiguation + cross-reference sinyali. (Wikidata Q139961515 2026-06-27
   * "spam" silindi → sameAs'ten düştü; yeniden ekleme kaynaklı olacak.)
   */
  sameAs?: string[];
  /**
   * Schema.org Organization.knowsAbout — Organization'ın expertise alanları.
   * AI Knowledge Graph + LLM training data + AI search cevaplarında "Riskmatik
   * neye uzman" sinyali. Genel kavramlar + mevzuat + metodoloji + modül adları.
   */
  knowsAbout?: string[];
  /**
   * Schema.org Organization.identifier — Google rich snippet doğrulama mührü.
   * ETBİS sicil + VKN birlikte yayınlanır (resmi sicil sinyali).
   */
  identifier: OrganizationIdentifier[];
  address: {
    '@type': 'PostalAddress';
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  contactPoint: {
    '@type': 'ContactPoint';
    contactType: string;
    email: string;
    telephone?: string;
    availableLanguage: string[];
    hoursAvailable?: string;
  };
  vatID?: string;
  taxID?: string;
}

/**
 * Tam Organization şeması — index.js gibi ana landing sayfaları için.
 * Override ile ek alanlar (sameAs, hasOfferCatalog vb.) eklenebilir.
 */
export function buildOrganizationSchema(overrides: OrgOverrides = {}) {
  const base: OrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: KURUMSAL_KIMLIK.marka,
    alternateName: 'Riskmatik İSG',   // 2008 Kardiyoloji SMS programı disambiguation
    legalName: KURUMSAL_KIMLIK.marka,  // Katman B — kurumsal görünüm
    url: KURUMSAL_KIMLIK.url,
    logo: KURUMSAL_KIMLIK.logo,
    foundingDate: KURUMSAL_KIMLIK.kurulusTarihi,
    description:
      "İş güvenliği uzmanları ve OSGB'ler için ISO 45001 çerçevesinde uçtan uca iş güvenliği yönetim platformu",
    slogan: 'Yapay zeka destekli ISO 45001 iş sağlığı ve güvenliği yönetim platformu',
    sameAs: [
      // SSoT: KURUMSAL_KIMLIK.sosyalMedya — sadece dolu (non-null) URL'ler dahil edilir.
      // Yeni hesap eklenirken KURUMSAL_KIMLIK güncellenir, schema otomatik günceller.
      KURUMSAL_KIMLIK.sosyalMedya.wikidata,
      KURUMSAL_KIMLIK.sosyalMedya.linkedin,
      KURUMSAL_KIMLIK.sosyalMedya.twitter,
      KURUMSAL_KIMLIK.sosyalMedya.youtube,
      KURUMSAL_KIMLIK.sosyalMedya.instagram,
    ].filter((url): url is string => Boolean(url)),
    knowsAbout: [
      // Standartlar
      'ISO 45001:2018 İş Sağlığı ve Güvenliği Yönetim Sistemi',
      // Risk metodolojileri
      'Fine-Kinney Risk Analizi Yöntemi',
      '5x5 Risk Matrisi',
      'Faaliyet Odaklı Risk Analizi (FORA)',
      // Modüller
      'İSG Saha Gözlem',
      'Düzeltici Faaliyet Yönetimi (DÖF)',
      'Acil Durum Eylem Planı',
      'Yıllık Çalışma Planı',
      'Yıllık Eğitim Planı',
      'İSG Kurulu Yönetimi',
      'Yönetim Gözden Geçirme (YGG)',
      'İş Kazası Kök Neden Analizi',
      // Mevzuat
      '6331 sayılı İş Sağlığı ve Güvenliği Kanunu',
      'Risk Değerlendirmesi Yönetmeliği (Resmî Gazete 28512)',
      'İşyerlerinde Acil Durumlar Hakkında Yönetmelik (Resmî Gazete 28681)',
      'İşyeri Tehlike Sınıfları Tebliği (Resmî Gazete 33211 / 2026)',
      'İSG Eğitim Yönetmeliği (Resmî Gazete 33212 / 2026)',
      'NACE Kodu Sınıflandırması',
      // Operasyonel
      'OSGB Yönetimi',
      'İş Güvenliği Uzmanı İş Akışı',
      // Yazılım kategorisi — entity'yi generik "İSG yazılımı" kategorisiyle ilişkilendir
      // (GEO #873: AI Overview "isg yazılımı/programı" cevabında incumbent'lar vardı, biz
      // yoktuk; factual kategori tanımı — "en iyi/lider" iddiası YOK, iddiali_laf kuralı).
      'İSG Yönetim Yazılımı',
      'İş Sağlığı ve Güvenliği Yazılımı',
      'OSGB Yazılımı',
      'Bulut Tabanlı İSG Yazılımı',
      'Yapay Zeka Destekli İSG Yönetim Yazılımı',
    ],
    identifier: [
      {
        '@type': 'PropertyValue',
        propertyID: 'ETBIS',
        name: 'ETBİS Sicil Kaydı',
        value: KURUMSAL_KIMLIK.etbis.siteId,
        url: KURUMSAL_KIMLIK.etbis.dogrulamaUrl,
        description:
          'T.C. Ticaret Bakanlığı Elektronik Ticaret Bilgi Sistemi kaydı',
      },
      {
        '@type': 'PropertyValue',
        propertyID: 'VKN',
        name: 'Vergi Kimlik Numarası',
        value: KURUMSAL_KIMLIK.vkn,
      },
    ],
    address: {
      '@type': 'PostalAddress',
      ...KURUMSAL_KIMLIK.adres,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: KURUMSAL_KIMLIK.destekEmail,
      ...(KURUMSAL_KIMLIK.telefon ? { telephone: KURUMSAL_KIMLIK.telefon } : {}),
      availableLanguage: ['Turkish'],
      hoursAvailable: KURUMSAL_KIMLIK.calismaSaatleriIso, // Schema.org openingHours
    },
    vatID: `TR${KURUMSAL_KIMLIK.vkn}`,                    // Schema.org vat field
    taxID: KURUMSAL_KIMLIK.vkn,                            // Türkiye VKN
  };
  return { ...base, ...overrides };
}

/**
 * Minimal Organization — SoftwareApplication.creator, Article.publisher,
 * CollectionPage.publisher gibi nested kullanımlar için.
 */
export function buildOrganizationMinimal() {
  return {
    '@type': 'Organization' as const,
    name: KURUMSAL_KIMLIK.marka,
    url: KURUMSAL_KIMLIK.url,
    logo: KURUMSAL_KIMLIK.logo,
  };
}
